// src/pages/Admin/LeaveTypesPage.jsx
//
// Leave Types master data
// =======================
//
// v17 architecture
// ----------------
// Leave Type core data and annual-summary field policies are edited together.
// The policy matrix controls User Settings checkmarks, correction dropdowns,
// backend validation and the year-closing rule for OPENING_BALANCE.  It replaces
// the previous growing list of `tracks_*` and `adjust_*` columns on leave_types.

import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import "./LeaveTypesPage.css";

import {
  fetchLeaveTypes,
  createLeaveType,
  updateLeaveType,
  toggleLeaveTypeActive,
} from "../../services/leaveTypesService";
import { isRequestCanceled } from "../../services/httpUtils";

function asBool(v) {
  if (v === true || v === false) return v;
  if (v == null) return false;
  const s = String(v).trim().toLowerCase();
  return s === "1" || s === "true" || s === "yes" || s === "y";
}

function normalizeCode(code) {
  return String(code || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_")
    .replace(/[^A-Z0-9_]/g, "");
}

function toNumberOrNull(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

const ACCOUNTING_TYPES = [
  "NO_BALANCE_EFFECT",
  "DEDUCT_FROM_LEAVE_BALANCE",
  "ADD_TIME_DEBT",
  "REDUCE_TIME_DEBT",
];

const YEAR_TRANSITION_MODES = [
  "NONE",
  "MANUAL_OPENING_ONLY",
  "CARRY_FORWARD_AVAILABLE",
  "CARRY_FORWARD_NET_TIME_BALANCE",
];

const BALANCE_CONSUMPTION_STRATEGIES = [
  "NONE",
  "CARRYOVER_FIRST",
  "CURRENT_YEAR_FIRST",
  "ANNUAL_LIMIT_ONLY",
];

const USAGE_MODE_POLICIES = [
  "NONE",
  "FIRST_REQUEST_LOCKS_YEAR_MODE",
];

const PAYROLL_RATE_PRESETS = [
  { value: "100", labelKey: "payrollRatePresetPaid100" },
  { value: "50", labelKey: "payrollRatePresetPaid50" },
  { value: "0", labelKey: "payrollRatePresetUnpaid0" },
  { value: "CUSTOM", labelKey: "payrollRatePresetCustom" },
];

const FIELD_KEYS = [
  "OPENING_BALANCE",
  "ENTITLEMENT",
  "ANNUAL_LIMIT",
  "DEDUCTED",
  "PAID_OUT",
  "FORFEITED",
  "TAKEN",
  "PLANNED",
  "AVAILABLE",
];

const SOURCE_TYPES = [
  "NONE",
  "USER_PROFILE",
  "LEAVE_TYPE_DEFAULT",
  "REQUESTS_EFFECTIVE",
  "REQUESTS_PENDING",
  "YEAR_CLOSING",
  "FORMULA",
  "ADJUSTMENTS_ONLY",
];

const FORMULA_KEYS = [
  "NONE",
  "LEAVE_BALANCE_AVAILABLE",
  "ANNUAL_LIMIT_AVAILABLE",
  "TOTAL_CURRENT_YEAR",
  "PLANNED_OR_TAKEN",
];


const UI_TEXT = {
  en: {
    labels: {
      code: "Code",
      namePl: "Name PL",
      nameEn: "Name EN",
      unit: "Unit",
      accounting: "Accounting",
      active: "Active",
      paid: "Paid",
      payrollExport: "Payroll export",
      payrollAbsenceCode: "Payroll absence code",
      payrollPaymentRate: "Payment rate",
      payrollPaymentRatePreset: "Payment rate preset",
      payrollRatePresetPaid100: "Paid 100%",
      payrollRatePresetPaid50: "Paid 50%",
      payrollRatePresetUnpaid0: "Unpaid 0%",
      payrollRatePresetCustom: "Custom",
      payrollSection: "Payroll / accounting export",
      payrollHelp: "The percentage is stored on approved requests as a payroll snapshot, so historical monthly reports do not change when this master data is modified later.",
      usageModePolicy: "Usage mode policy",
      usageModePolicyHelp: "Controls whether the first submitted request in a year locks DAYS or HOURS mode for this employee and Leave Type. Use for legal allowances such as force majeure.",
      requiresApproval: "Requires approval",
      partialDay: "Partial day",
      showInCalendar: "Show in calendar",
      annualSummaryFieldPolicies: "Annual summary field policies",
      track: "Track",
      canAdjust: "Can adjust",
      source: "Source",
      formula: "Formula",
      yearTransition: "Year transition",
      balanceConsumption: "Balance consumption",
      balanceSource: "Balance source",
      balanceSourceOwn: "Own balance (self)",
      balanceSourceHelp: "Optional source Leave Type whose buckets are consumed by this request type. Use it for hybrid types such as Leave on request: own annual limit, but vacation balance consumption.",
      default: "Default",
      defaultDays: "Default days",
      defaultHours: "Default hours",
      missing: "Missing value",
      enforcement: "Enforcement",
      field: "Field",
      currentRecommendation: "Current recommendation",
      applyRecommended: "Apply recommended policies",
      sourceDescriptions: "Source and formula descriptions",
      methodologyNote: "Track means that the value is maintained and shown in User Settings. Can adjust means that a superuser may create an audited correction for this maintained value.",
      policyContractNote: "The Source, Formula and Year transition columns are the calculation contract used by User Settings and backend validation.",
      legalHrNote: "Legal/HR note: annual limits such as force majeure or childcare limits restrict usage of that specific Leave Type. They do not increase the employee vacation entitlement. Please confirm final values with HR/legal before production use.",
      technicalCode: "Technical code",
      noFormula: "No formula is applied.",
      policyCardsIntro: "Each summary field is configured as a separate card. Open one field at a time to avoid horizontal scrolling and to keep the calculation method visible.",
      trackingAndAdjustment: "Tracking and adjustment",
      calculationSource: "Calculation source",
      defaultAndValidation: "Default and validation",
      formulaPreview: "Formula preview",
      openDetails: "Open details",
      closeDetails: "Close details",
      tracked: "Tracked",
      notTracked: "Not tracked",
      adjustable: "Adjustable",
      notAdjustable: "Not adjustable",
      confirmApplyRecommended: "Apply recommended policies for this accounting type? This will replace the current field-policy settings in the editor.",
    },
    accountingTypes: {
      NO_BALANCE_EFFECT: "Usage only / no balance",
      DEDUCT_FROM_LEAVE_BALANCE: "Deducts leave balance",
      ADD_TIME_DEBT: "Adds time debt",
      REDUCE_TIME_DEBT: "Reduces time debt",
    },
    accountingHelp: {
      NO_BALANCE_EFFECT: "Workflow/calendar/usage-only event. It is reported but does not consume vacation balance and does not create or reduce make-up-time debt.",
      DEDUCT_FROM_LEAVE_BALANCE: "Classical absence accounting. Effective/pending request duration contributes to leave balance or annual-limit usage according to field policies.",
      ADD_TIME_DEBT: "Creates time that the employee should make up later, for example a private exit during working time.",
      REDUCE_TIME_DEBT: "Records make-up work that reduces previously created time debt.",
    },
    fieldKeys: {
      OPENING_BALANCE: "Opening balance",
      ENTITLEMENT: "Entitlement",
      ANNUAL_LIMIT: "Annual limit",
      DEDUCTED: "Deducted",
      PAID_OUT: "Paid out",
      FORFEITED: "Forfeited",
      TAKEN: "Taken / used",
      PLANNED: "Planned",
      AVAILABLE: "Available",
    },
    fieldHelp: {
      OPENING_BALANCE: "Value opening the year. For vacation it can be carryover; for work-time debt it can be opening time debt or credit.",
      ENTITLEMENT: "Annual entitlement, normally from the user leave profile. This increases the balance of the relevant Leave Type.",
      ANNUAL_LIMIT: "Annual legal/business limit for using this Leave Type. It limits usage of this type and does not increase vacation entitlement.",
      DEDUCTED: "Manual/administrative deduction or correction value.",
      PAID_OUT: "Value paid out or settled financially.",
      FORFEITED: "Value that expired or was forfeited.",
      TAKEN: "Effective usage, usually calculated from effective requests.",
      PLANNED: "Planned or pending usage, usually calculated from pending requests.",
      AVAILABLE: "Calculated remaining value. The exact formula is selected by Formula key.",
    },
    sourceTypes: {
      NONE: "None",
      USER_PROFILE: "User profile",
      LEAVE_TYPE_DEFAULT: "Leave Type default",
      REQUESTS_EFFECTIVE: "Effective requests",
      REQUESTS_PENDING: "Pending requests",
      YEAR_CLOSING: "Year closing",
      FORMULA: "Formula",
      ADJUSTMENTS_ONLY: "Manual adjustments only",
    },
    sourceHelp: {
      NONE: "No automatic value is calculated for this field.",
      USER_PROFILE: "The value is read from the employee leave profile, for example annual vacation allowance.",
      LEAVE_TYPE_DEFAULT: "The value is taken from the default value configured in this policy row.",
      REQUESTS_EFFECTIVE: "Approval-aware request usage. If approval is required, only approved requests count. If approval is not required, valid non-cancelled requests are effective immediately.",
      REQUESTS_PENDING: "Pending requests waiting for approval. For types without approval, pending requests should normally become effective immediately instead of staying planned.",
      YEAR_CLOSING: "The value is generated by the year-closing / carry-forward process.",
      FORMULA: "The value is calculated from other summary fields. Select a concrete Formula key so the calculation is explicit.",
      ADJUSTMENTS_ONLY: "No automatic value is calculated. The value exists only through audited manual corrections.",
    },
    formulaKeys: {
      NONE: "None",
      LEAVE_BALANCE_AVAILABLE: "Leave balance available",
      ANNUAL_LIMIT_AVAILABLE: "Annual limit available",
      TOTAL_CURRENT_YEAR: "Total current year",
      PLANNED_OR_TAKEN: "Planned or taken",
    },
    formulaHelp: {
      NONE: "No formula is applied.",
      LEAVE_BALANCE_AVAILABLE: "Available = opening balance + entitlement + deducted - paid out - forfeited - taken - planned.",
      ANNUAL_LIMIT_AVAILABLE: "Available = annual limit + deducted - forfeited - taken - planned. This limits usage of the specific Leave Type and does not increase vacation entitlement.",
      TOTAL_CURRENT_YEAR: "Total = opening balance + entitlement + annual limit + deducted.",
      PLANNED_OR_TAKEN: "Planned or taken = taken + planned.",
    },
    missingPolicies: {
      IGNORE: "Ignore",
      USE_ZERO: "Use zero",
      WARNING: "Warning",
      ERROR: "Error",
    },
    enforcementModes: {
      NONE: "No enforcement",
      WARN: "Warn only",
      ENFORCE: "Block / enforce",
    },
    balanceConsumptionStrategies: {
      NONE: "No balance buckets",
      CARRYOVER_FIRST: "Carryover first",
      CURRENT_YEAR_FIRST: "Current year first",
      ANNUAL_LIMIT_ONLY: "Annual limit only",
    },
    balanceConsumptionHelp: {
      NONE: "This Leave Type does not create vacation balance bucket allocations.",
      CARRYOVER_FIRST: "Approved requests consume carryover/opening balance before current-year entitlement. This is the recommended vacation strategy.",
      CURRENT_YEAR_FIRST: "Approved requests consume current-year entitlement before carryover. Use only if HR explicitly requires it.",
      ANNUAL_LIMIT_ONLY: "Approved requests consume the Leave Type annual-limit bucket. This is useful for legally limited event types that do not increase vacation entitlement.",
    },
    usageModePolicies: {
      NONE: "No usage-mode lock",
      FIRST_REQUEST_LOCKS_YEAR_MODE: "First request locks DAYS/HOURS for the year",
    },
    yearTransitionModes: {
      NONE: "None",
      MANUAL_OPENING_ONLY: "Manual opening only",
      CARRY_FORWARD_AVAILABLE: "Carry forward available",
      CARRY_FORWARD_NET_TIME_BALANCE: "Carry forward net time balance",
    },
    recommendations: {
      DEDUCT_FROM_LEAVE_BALANCE: "Recommended for classical leave balances. Taken uses effective requests, planned uses pending requests, and available is calculated by an explicit formula. For legal annual-limit absences, track Annual limit and use the Annual limit available formula instead of Entitlement.",
      NO_BALANCE_EFFECT: "Recommended for usage-only events such as business trips or informational HR events. Usually only Taken and Planned are tracked. No vacation entitlement is increased or reduced.",
      ADD_TIME_DEBT: "Recommended for private exits to be made up later. Opening balance and effective/pending usage are tracked in hours. The net work-time balance is shown as a separate read-only summary.",
      REDUCE_TIME_DEBT: "Recommended for make-up work. It reduces previously created time debt. Opening balance can represent carried-forward time credit when year closing is configured.",
      default: "Select an accounting type to get recommended policy guidance.",
    },
  },
  pl: {
    labels: {
      code: "Kod",
      namePl: "Nazwa PL",
      nameEn: "Nazwa EN",
      unit: "Jednostka",
      accounting: "Księgowanie",
      active: "Aktywny",
      paid: "Płatne",
      payrollExport: "Eksport do płac",
      payrollAbsenceCode: "Kod absencji do płac",
      payrollPaymentRate: "Procent płatności",
      payrollPaymentRatePreset: "Preset płatności",
      payrollRatePresetPaid100: "Płatne 100%",
      payrollRatePresetPaid50: "Płatne 50%",
      payrollRatePresetUnpaid0: "Niepłatne 0%",
      payrollRatePresetCustom: "Własny",
      payrollSection: "Płace / eksport księgowy",
      payrollHelp: "Procent jest zapisywany na zatwierdzonym wniosku jako snapshot płacowy, aby historyczne raporty miesięczne nie zmieniały się po późniejszej zmianie master data.",
      usageModePolicy: "Polityka trybu wykorzystania",
      usageModePolicyHelp: "Określa, czy pierwszy złożony wniosek w roku blokuje tryb DNI albo GODZINY dla pracownika i typu nieobecności. Użyj dla uprawnień takich jak siła wyższa.",
      requiresApproval: "Wymaga akceptacji",
      partialDay: "Część dnia",
      showInCalendar: "Pokazuj w kalendarzu",
      annualSummaryFieldPolicies: "Polityki pól rocznego podsumowania",
      track: "Prowadź",
      canAdjust: "Można korygować",
      source: "Źródło",
      formula: "Formuła",
      yearTransition: "Przeniesienie roku",
      balanceConsumption: "Zużycie salda",
      balanceSource: "Saldo źródłowe",
      balanceSourceOwn: "Własne saldo tego typu",
      balanceSourceHelp: "Opcjonalny typ urlopu, którego pule salda są pomniejszane przez ten request. Użyj dla typów hybrydowych, np. urlop na żądanie: własny limit roczny, ale zużycie salda urlopu wypoczynkowego.",
      default: "Domyślnie",
      defaultDays: "Domyślnie dni",
      defaultHours: "Domyślnie godziny",
      missing: "Brak wartości",
      enforcement: "Egzekwowanie",
      field: "Pole",
      currentRecommendation: "Rekomendacja dla bieżącej konfiguracji",
      applyRecommended: "Zastosuj rekomendowane polityki",
      sourceDescriptions: "Opisy źródeł i formuł",
      methodologyNote: "Prowadź oznacza, że wartość jest utrzymywana i pokazywana w ustawieniach użytkownika. Można korygować oznacza, że superuser może utworzyć audytowalną korektę tej prowadzonej wartości.",
      policyContractNote: "Kolumny Źródło, Formuła i Przeniesienie roku są kontraktem kalkulacyjnym używanym przez User Settings i walidację backendu.",
      legalHrNote: "Uwaga HR/prawna: limity roczne, np. siła wyższa lub opieka nad dzieckiem, ograniczają użycie konkretnego typu nieobecności. Nie zwiększają salda urlopu wypoczynkowego. Przed produkcją potwierdź ostateczne wartości z HR/prawnie.",
      technicalCode: "Kod techniczny",
      noFormula: "Brak formuły.",
      policyCardsIntro: "Każde pole rocznego podsumowania jest konfigurowane jako osobna karta. Otwórz jedno pole naraz, aby uniknąć poziomego scrolla i widzieć sposób kalkulacji.",
      trackingAndAdjustment: "Prowadzenie i korekta",
      calculationSource: "Źródło kalkulacji",
      defaultAndValidation: "Wartości domyślne i walidacja",
      formulaPreview: "Podgląd formuły",
      openDetails: "Otwórz szczegóły",
      closeDetails: "Zamknij szczegóły",
      tracked: "Prowadzone",
      notTracked: "Nieprowadzone",
      adjustable: "Korygowalne",
      notAdjustable: "Bez korekty",
      confirmApplyRecommended: "Zastosować rekomendowane polityki dla tego typu księgowania? Bieżące ustawienia polityk pól w edytorze zostaną zastąpione.",
    },
    accountingTypes: {
      NO_BALANCE_EFFECT: "Tylko ewidencja / bez salda",
      DEDUCT_FROM_LEAVE_BALANCE: "Pomniejsza saldo nieobecności",
      ADD_TIME_DEBT: "Dodaje dług czasu",
      REDUCE_TIME_DEBT: "Zmniejsza dług czasu",
    },
    accountingHelp: {
      NO_BALANCE_EFFECT: "Zdarzenie workflow/kalendarzowe/ewidencyjne. Jest raportowane, ale nie zużywa salda urlopu i nie tworzy ani nie redukuje długu czasu.",
      DEDUCT_FROM_LEAVE_BALANCE: "Klasyczne księgowanie nieobecności. Czas skutecznych/oczekujących requestów wpływa na saldo lub limit roczny zgodnie z politykami pól.",
      ADD_TIME_DEBT: "Tworzy czas, który pracownik powinien później odpracować, np. wyjście prywatne w czasie pracy.",
      REDUCE_TIME_DEBT: "Rejestruje odpracowanie, które zmniejsza wcześniej utworzony dług czasu.",
    },
    fieldKeys: {
      OPENING_BALANCE: "Bilans otwarcia",
      ENTITLEMENT: "Uprawnienie",
      ANNUAL_LIMIT: "Limit roczny",
      DEDUCTED: "Odjęte",
      PAID_OUT: "Wypłacone",
      FORFEITED: "Przepadłe",
      TAKEN: "Wykorzystane",
      PLANNED: "Planowane",
      AVAILABLE: "Dostępne",
    },
    fieldHelp: {
      OPENING_BALANCE: "Wartość otwierająca rok. Dla urlopu może oznaczać saldo przeniesione, a dla długu czasu początkowy dług lub kredyt czasu.",
      ENTITLEMENT: "Roczne uprawnienie, zwykle z profilu użytkownika. Powiększa saldo danego typu.",
      ANNUAL_LIMIT: "Roczny limit prawny/biznesowy dla użycia tego typu. Ogranicza użycie tego typu i nie zwiększa urlopu wypoczynkowego.",
      DEDUCTED: "Ręczne/administracyjne odjęcie lub korekta wartości.",
      PAID_OUT: "Wartość wypłacona lub rozliczona finansowo.",
      FORFEITED: "Wartość, która wygasła lub przepadła.",
      TAKEN: "Skuteczne wykorzystanie, zwykle liczone ze skutecznych requestów.",
      PLANNED: "Planowane lub oczekujące wykorzystanie, zwykle liczone z oczekujących requestów.",
      AVAILABLE: "Wartość pozostała do wykorzystania. Dokładna formuła jest określona przez klucz formuły.",
    },
    sourceTypes: {
      NONE: "Brak",
      USER_PROFILE: "Profil użytkownika",
      LEAVE_TYPE_DEFAULT: "Domyślna wartość typu",
      REQUESTS_EFFECTIVE: "Skuteczne requesty",
      REQUESTS_PENDING: "Oczekujące requesty",
      YEAR_CLOSING: "Zamknięcie roku",
      FORMULA: "Formuła",
      ADJUSTMENTS_ONLY: "Tylko korekty ręczne",
    },
    sourceHelp: {
      NONE: "Dla tego pola nie jest liczona automatyczna wartość.",
      USER_PROFILE: "Wartość jest pobierana z profilu urlopowego pracownika, np. roczny wymiar urlopu.",
      LEAVE_TYPE_DEFAULT: "Wartość jest pobierana z domyślnej wartości skonfigurowanej w tym wierszu polityki.",
      REQUESTS_EFFECTIVE: "Użycie requestów zależne od akceptacji. Jeśli akceptacja jest wymagana, liczą się tylko zatwierdzone requesty. Jeśli akceptacja nie jest wymagana, ważne nieanulowane requesty są skuteczne od razu.",
      REQUESTS_PENDING: "Requesty oczekujące na akceptację. Dla typów bez akceptacji requesty zwykle powinny od razu stać się skuteczne zamiast pozostać planowane.",
      YEAR_CLOSING: "Wartość jest generowana przez proces zamknięcia roku / przeniesienia sald.",
      FORMULA: "Wartość jest liczona z innych pól summary. Wybierz konkretny klucz formuły, aby kalkulacja była jawna.",
      ADJUSTMENTS_ONLY: "Nie jest liczona automatyczna wartość. Wartość istnieje tylko przez audytowalne korekty ręczne.",
    },
    formulaKeys: {
      NONE: "Brak",
      LEAVE_BALANCE_AVAILABLE: "Dostępne saldo nieobecności",
      ANNUAL_LIMIT_AVAILABLE: "Dostępny limit roczny",
      TOTAL_CURRENT_YEAR: "Suma roku bieżącego",
      PLANNED_OR_TAKEN: "Planowane lub wykorzystane",
    },
    formulaHelp: {
      NONE: "Brak formuły.",
      LEAVE_BALANCE_AVAILABLE: "Dostępne = bilans otwarcia + uprawnienie + odjęte - wypłacone - przepadłe - wykorzystane - planowane.",
      ANNUAL_LIMIT_AVAILABLE: "Dostępne = limit roczny + odjęte - przepadłe - wykorzystane - planowane. To ogranicza użycie konkretnego typu i nie zwiększa urlopu wypoczynkowego.",
      TOTAL_CURRENT_YEAR: "Suma = bilans otwarcia + uprawnienie + limit roczny + odjęte.",
      PLANNED_OR_TAKEN: "Planowane lub wykorzystane = wykorzystane + planowane.",
    },
    missingPolicies: {
      IGNORE: "Ignoruj",
      USE_ZERO: "Użyj zera",
      WARNING: "Ostrzeżenie",
      ERROR: "Błąd",
    },
    enforcementModes: {
      NONE: "Bez egzekwowania",
      WARN: "Tylko ostrzeżenie",
      ENFORCE: "Blokuj / egzekwuj",
    },
    balanceConsumptionStrategies: {
      NONE: "Bez pul salda",
      CARRYOVER_FIRST: "Najpierw zaległy",
      CURRENT_YEAR_FIRST: "Najpierw bieżący",
      ANNUAL_LIMIT_ONLY: "Tylko limit roczny",
    },
    balanceConsumptionHelp: {
      NONE: "Ten typ nie tworzy alokacji do pul salda urlopowego.",
      CARRYOVER_FIRST: "Zatwierdzone wnioski zużywają najpierw urlop zaległy / saldo otwarcia, a dopiero potem uprawnienie roku bieżącego. To zalecana strategia dla urlopu wypoczynkowego.",
      CURRENT_YEAR_FIRST: "Zatwierdzone wnioski zużywają najpierw uprawnienie roku bieżącego, a potem zaległy. Używać tylko, jeśli HR tego wymaga.",
      ANNUAL_LIMIT_ONLY: "Zatwierdzone wnioski zużywają pulę limitu rocznego danego typu. Przydatne dla limitowanych typów absencji, które nie zwiększają urlopu wypoczynkowego.",
    },
    usageModePolicies: {
      NONE: "Bez blokady trybu",
      FIRST_REQUEST_LOCKS_YEAR_MODE: "Pierwszy wniosek blokuje DNI/GODZINY na rok",
    },
    yearTransitionModes: {
      NONE: "Brak",
      MANUAL_OPENING_ONLY: "Tylko ręczne otwarcie",
      CARRY_FORWARD_AVAILABLE: "Przenieś dostępne saldo",
      CARRY_FORWARD_NET_TIME_BALANCE: "Przenieś saldo netto czasu",
    },
    recommendations: {
      DEDUCT_FROM_LEAVE_BALANCE: "Rekomendowane dla klasycznych sald nieobecności. Wykorzystane używa skutecznych requestów, planowane używa oczekujących requestów, a dostępne jest liczone jawną formułą. Dla nieobecności z rocznym limitem prawnym prowadź Limit roczny i użyj formuły Dostępny limit roczny zamiast Uprawnienia.",
      NO_BALANCE_EFFECT: "Rekomendowane dla zdarzeń tylko ewidencyjnych, np. podróży służbowych lub informacyjnych zdarzeń HR. Zwykle prowadzone są tylko Wykorzystane i Planowane. Nie zwiększa ani nie zmniejsza urlopu wypoczynkowego.",
      ADD_TIME_DEBT: "Rekomendowane dla wyjść prywatnych do odpracowania. Bilans otwarcia i skuteczne/oczekujące użycie są prowadzone w godzinach. Saldo netto czasu pracy jest pokazywane jako osobne podsumowanie tylko do odczytu.",
      REDUCE_TIME_DEBT: "Rekomendowane dla odpracowania. Zmniejsza wcześniej utworzony dług czasu. Bilans otwarcia może oznaczać przeniesiony kredyt czasu, jeśli skonfigurowano zamknięcie roku.",
      default: "Wybierz księgowanie, aby zobaczyć rekomendację polityk.",
    },
  },
};

function resolveLang(language) {
  return String(language || "en").toLowerCase().startsWith("pl") ? "pl" : "en";
}

function ui(language, section, key, fallback) {
  const lang = resolveLang(language);
  return UI_TEXT[lang]?.[section]?.[key] || UI_TEXT.en?.[section]?.[key] || fallback || key;
}

function sourceLabel(sourceType, language) {
  return ui(language, "sourceTypes", sourceType, sourceType || "None");
}

function sourceHelp(sourceType, language) {
  return ui(language, "sourceHelp", sourceType, "");
}

function formulaLabel(formulaKey, language) {
  return ui(language, "formulaKeys", formulaKey, formulaKey || "None");
}

function formulaHelp(formulaKey, language) {
  return ui(language, "formulaHelp", formulaKey, "");
}

function fieldLabel(fieldKey, language) {
  return ui(language, "fieldKeys", fieldKey, fieldKey);
}

function fieldHelp(fieldKey, language) {
  return ui(language, "fieldHelp", fieldKey, "");
}

function yearTransitionLabel(mode, language) {
  return ui(language, "yearTransitionModes", mode, mode || "None");
}

function missingPolicyLabel(mode, language) {
  return ui(language, "missingPolicies", mode, mode || "Use zero");
}

function enforcementModeLabel(mode, language) {
  return ui(language, "enforcementModes", mode, mode || "None");
}

function accountingLabel(accountingType, language) {
  return ui(language, "accountingTypes", accountingType, accountingType || "");
}

function accountingHelp(accountingType, language) {
  return ui(language, "accountingHelp", accountingType, "");
}

function recommendedPolicyExplanation(accountingType, language) {
  return ui(language, "recommendations", accountingType, ui(language, "recommendations", "default", "Select an accounting type to get recommended policy guidance."));
}

const MISSING_POLICIES = ["IGNORE", "USE_ZERO", "WARNING", "ERROR"];
const ENFORCEMENT_MODES = ["NONE", "WARN", "ENFORCE"];

function normalizePolicyList(rawPolicies = [], form = {}) {
  const byKey = new Map((Array.isArray(rawPolicies) ? rawPolicies : []).map((p) => [String(p.field_key).toUpperCase(), p]));
  const accounting = String(form.balance_accounting_type || "DEDUCT_FROM_LEAVE_BALANCE");
  const isLeaveBalance = accounting === "DEDUCT_FROM_LEAVE_BALANCE";
  const isWorkTime = accounting === "ADD_TIME_DEBT" || accounting === "REDUCE_TIME_DEBT";

  return FIELD_KEYS.map((fieldKey, index) => {
    const existing = byKey.get(fieldKey) || {};
    let defaults = {
      field_key: fieldKey,
      tracks_value: false,
      can_adjust_value: false,
      source_type: "NONE",
      formula_key: "NONE",
      default_value: "",
      default_value_days: "",
      default_value_hours: "",
      missing_value_policy: "IGNORE",
      enforcement_mode: "NONE",
      year_transition_mode: "NONE",
      sort_order: index + 1,
    };

    if (fieldKey === "TAKEN") defaults = { ...defaults, tracks_value: true, can_adjust_value: true, source_type: "REQUESTS_EFFECTIVE", missing_value_policy: "USE_ZERO" };
    if (fieldKey === "PLANNED") defaults = { ...defaults, tracks_value: true, can_adjust_value: true, source_type: "REQUESTS_PENDING", missing_value_policy: "USE_ZERO" };
    if (fieldKey === "AVAILABLE" && isLeaveBalance) defaults = { ...defaults, tracks_value: true, can_adjust_value: false, source_type: "FORMULA", formula_key: "LEAVE_BALANCE_AVAILABLE", missing_value_policy: "ERROR", enforcement_mode: "ENFORCE" };
    if (fieldKey === "OPENING_BALANCE" && isWorkTime) defaults = { ...defaults, tracks_value: true, can_adjust_value: true, source_type: "YEAR_CLOSING", year_transition_mode: "CARRY_FORWARD_NET_TIME_BALANCE", missing_value_policy: "USE_ZERO" };

    return {
      ...defaults,
      ...existing,
      field_key: fieldKey,
      tracks_value: existing.tracks_value != null ? asBool(existing.tracks_value) : defaults.tracks_value,
      can_adjust_value: existing.can_adjust_value != null ? asBool(existing.can_adjust_value) : defaults.can_adjust_value,
      formula_key: existing.formula_key ?? defaults.formula_key,
      default_value: existing.default_value ?? defaults.default_value,
      default_value_days: existing.default_value_days ?? defaults.default_value_days,
      default_value_hours: existing.default_value_hours ?? defaults.default_value_hours,
      year_transition_mode: fieldKey === "OPENING_BALANCE" ? (existing.year_transition_mode ?? defaults.year_transition_mode) : "NONE",
    };
  });
}

function initialForm() {
  const base = {
    code: "",
    name_pl: "",
    name_en: "",
    active: true,
    is_paid: true,
    payroll_export_enabled: true,
    payroll_absence_code: "",
    payroll_payment_rate_percent: "100.00",
    usage_mode_policy: "NONE",
    requires_approval: true,
    supports_partial_day: false,
    show_in_calendar: true,
    unit: "DAYS",
    balance_accounting_type: "DEDUCT_FROM_LEAVE_BALANCE",
    balance_source_leave_type_id: "",
    balance_consumption_strategy: "CARRYOVER_FIRST",
  };
  return { ...base, fieldPolicies: normalizePolicyList([], base) };
}

function policySummary(row) {
  const policies = row.fieldPolicies || row.field_policies || [];
  const tracked = policies.filter((p) => asBool(p.tracks_value)).length;
  const adjustable = policies.filter((p) => asBool(p.can_adjust_value)).length;
  return `${tracked}/${adjustable}`;
}

function normalizeRateInput(value, fallback = "100.00") {
  if (value == null || value === "") return fallback;
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return String(Math.max(0, Math.min(100, Math.round(n * 100) / 100)));
}

function inferPayrollRatePreset(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "CUSTOM";
  if (Math.abs(n - 100) < 0.001) return "100";
  if (Math.abs(n - 50) < 0.001) return "50";
  if (Math.abs(n - 0) < 0.001) return "0";
  return "CUSTOM";
}


export default function LeaveTypesPage() {
  const { t, i18n } = useTranslation(["common", "leaves"]);
  const lang = i18n.language;

  // v24 QA fix:
  // The v23 stabilization batch accidentally kept the policy-matrix labels only
  // in a local JavaScript dictionary and also removed helper functions used by
  // the component.  The local dictionary is still kept below as a defensive
  // fallback, but the visible labels now first resolve through the standard
  // leaves.json i18n namespace.  This prevents raw enum values and missing
  // translation keys from becoming the primary UI text.
  const policyText = (section, key, fallback) =>
    t(`leaves:leaveTypes.policyMatrix.${section}.${key}`, {
      defaultValue: ui(lang, section, key, fallback),
    });
  const policyLabel = (key, fallback) => policyText("labels", key, fallback);
  const policyAccountingLabel = (key) => policyText("accountingTypes", key, accountingLabel(key, lang));
  const policyAccountingHelp = (key) => policyText("accountingHelp", key, accountingHelp(key, lang));
  const policyFieldLabel = (key) => policyText("fieldKeys", key, fieldLabel(key, lang));
  const policyFieldHelp = (key) => policyText("fieldHelp", key, fieldHelp(key, lang));
  const policySourceLabel = (key) => policyText("sourceTypes", key, sourceLabel(key, lang));
  const policySourceHelp = (key) => policyText("sourceHelp", key, sourceHelp(key, lang));
  const policyFormulaLabel = (key) => policyText("formulaKeys", key, formulaLabel(key, lang));
  const policyFormulaHelp = (key) => policyText("formulaHelp", key, formulaHelp(key, lang));
  const policyMissingLabel = (key) => policyText("missingPolicies", key, missingPolicyLabel(key, lang));
  const policyEnforcementLabel = (key) => policyText("enforcementModes", key, enforcementModeLabel(key, lang));
  const policyYearTransitionLabel = (key) => policyText("yearTransitionModes", key, yearTransitionLabel(key, lang));
  const policyConsumptionLabel = (key) => policyText("balanceConsumptionStrategies", key, key);
  const policyConsumptionHelp = (key) => policyText("balanceConsumptionHelp", key, "");
  const policyUsageModeLabel = (key) => policyText("usageModePolicies", key, key);
  const policyRecommendation = (key) => policyText("recommendations", key, recommendedPolicyExplanation(key, lang));

  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState("CREATE");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(initialForm());
  const [expandedPolicyKey, setExpandedPolicyKey] = useState("OPENING_BALANCE");

  const activeCount = useMemo(() => rows.filter((r) => asBool(r.active)).length, [rows]);

  const balanceSourceOptions = useMemo(() => {
    const currentId = editingId ? Number(editingId) : null;
    return rows
      .filter((r) => Number(r.id) !== currentId)
      .map((r) => ({
        id: String(r.id),
        label: `${r.code} — ${lang === "pl" ? (r.name_pl || r.name_en) : (r.name_en || r.name_pl)}`,
      }));
  }, [rows, editingId, lang]);

  function formatBalanceSource(row) {
    const source = row?.balanceSourceLeaveType || row?.balance_source_leave_type || null;
    if (!row?.balance_source_leave_type_id || !source) return policyLabel("balanceSourceOwn");
    return `${source.code} — ${lang === "pl" ? (source.name_pl || source.name_en) : (source.name_en || source.name_pl)}`;
  }

  async function load(signal) {
    setLoading(true);
    setError("");
    try {
      const data = await fetchLeaveTypes({ signal });
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      if (isRequestCanceled(e)) return;
      setError(e?.response?.data?.message || t("leaves:leaveTypes.loadError", { defaultValue: "Failed to load leave types." }));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const ac = new AbortController();
    load(ac.signal);
    return () => ac.abort();
    
  }, []);

  function patch(next) {
    setForm((prev) => ({ ...prev, ...next }));
  }

  function patchPolicy(fieldKey, next) {
    setForm((prev) => ({
      ...prev,
      fieldPolicies: normalizePolicyList(prev.fieldPolicies, prev).map((p) => {
        if (p.field_key !== fieldKey) return p;
        const merged = { ...p, ...next };
        if (!asBool(merged.tracks_value)) merged.can_adjust_value = false;
        return merged;
      }),
    }));
  }

  function setPayrollRate(value) {
    const normalized = normalizeRateInput(value, "0.00");
    patch({
      payroll_payment_rate_percent: normalized,
      is_paid: Number(normalized || 0) > 0,
    });
  }

  function applyPayrollPreset(value) {
    if (value === "CUSTOM") return;
    setPayrollRate(value);
  }

  function applyRecommendedPolicies() {
    const confirmed = window.confirm(policyLabel("confirmApplyRecommended"));
    if (!confirmed) return;
    setForm((prev) => ({ ...prev, fieldPolicies: normalizePolicyList([], prev) }));
    setExpandedPolicyKey("OPENING_BALANCE");
  }

  function openCreate() {
    setEditorMode("CREATE");
    setEditingId(null);
    setForm(initialForm());
    setExpandedPolicyKey("OPENING_BALANCE");
    setEditorOpen(true);
    setError("");
    setSuccess("");
  }

  function openEdit(row) {
    const base = {
      code: row.code || "",
      name_pl: row.name_pl || "",
      name_en: row.name_en || "",
      active: asBool(row.active),
      is_paid: asBool(row.is_paid),
      payroll_export_enabled: row.payroll_export_enabled == null ? true : asBool(row.payroll_export_enabled),
      payroll_absence_code: row.payroll_absence_code || row.code || "",
      payroll_payment_rate_percent: normalizeRateInput(row.payroll_payment_rate_percent, asBool(row.is_paid) ? "100.00" : "0.00"),
      usage_mode_policy: row.usage_mode_policy || "NONE",
      requires_approval: asBool(row.requires_approval),
      supports_partial_day: asBool(row.supports_partial_day),
      show_in_calendar: asBool(row.show_in_calendar),
      unit: row.unit || "DAYS",
      balance_accounting_type: row.balance_accounting_type || "DEDUCT_FROM_LEAVE_BALANCE",
      balance_source_leave_type_id: row.balance_source_leave_type_id ? String(row.balance_source_leave_type_id) : "",
      balance_consumption_strategy: row.balance_consumption_strategy || "CARRYOVER_FIRST",
    };

    setEditorMode("EDIT");
    setEditingId(Number(row.id));
    setForm({ ...base, fieldPolicies: normalizePolicyList(row.fieldPolicies || row.field_policies || [], base) });
    setExpandedPolicyKey("OPENING_BALANCE");
    setEditorOpen(true);
    setError("");
    setSuccess("");
  }

  async function submit() {
    setError("");
    setSuccess("");

    const code = normalizeCode(form.code);
    if (!code) return setError(t("leaves:leaveTypes.validation.codeRequired", { defaultValue: "Code is required." }));
    if (!form.name_pl || !form.name_en) return setError(t("leaves:leaveTypes.validation.namesRequired", { defaultValue: "Both PL and EN names are required." }));

    const fieldPolicies = normalizePolicyList(form.fieldPolicies, form).map((p) => ({
      ...p,
      default_value: toNumberOrNull(p.default_value),
      default_value_days: toNumberOrNull(p.default_value_days),
      default_value_hours: toNumberOrNull(p.default_value_hours),
    }));

    const payload = {
      code,
      name_pl: form.name_pl,
      name_en: form.name_en,
      active: asBool(form.active),
      is_paid: Number(form.payroll_payment_rate_percent || 0) > 0,
      payroll_export_enabled: asBool(form.payroll_export_enabled),
      payroll_absence_code: form.payroll_absence_code || code,
      payroll_payment_rate_percent: Number(normalizeRateInput(form.payroll_payment_rate_percent, "100.00")),
      usage_mode_policy: form.usage_mode_policy || "NONE",
      requires_approval: asBool(form.requires_approval),
      supports_partial_day: asBool(form.supports_partial_day),
      show_in_calendar: asBool(form.show_in_calendar),
      unit: form.unit || "DAYS",
      balance_accounting_type: form.balance_accounting_type || "DEDUCT_FROM_LEAVE_BALANCE",
      balance_source_leave_type_id: form.balance_source_leave_type_id ? Number(form.balance_source_leave_type_id) : null,
      balance_consumption_strategy: form.balance_consumption_strategy || "CARRYOVER_FIRST",
      fieldPolicies,
    };

    try {
      if (editorMode === "CREATE") await createLeaveType(payload);
      else await updateLeaveType(editingId, payload);
      setEditorOpen(false);
      setSuccess(t("common:messages.saved", { defaultValue: "Saved." }));
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || t("leaves:leaveTypes.saveError", { defaultValue: "Failed to save leave type." }));
    }
  }

  async function toggle(row) {
    try {
      await toggleLeaveTypeActive(row.id, !asBool(row.active));
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || t("leaves:leaveTypes.saveError", { defaultValue: "Failed to update leave type." }));
    }
  }

  const normalizedPolicies = normalizePolicyList(form.fieldPolicies, form);

  function policyBadges(p) {
    return (
      <div className="policy-card-badges" aria-label="policy status">
        <span className={asBool(p.tracks_value) ? "policy-badge is-on" : "policy-badge is-off"}>
          {asBool(p.tracks_value) ? policyLabel("tracked") : policyLabel("notTracked")}
        </span>
        <span className={asBool(p.can_adjust_value) ? "policy-badge is-on" : "policy-badge is-off"}>
          {asBool(p.can_adjust_value) ? policyLabel("adjustable") : policyLabel("notAdjustable")}
        </span>
        <span className="policy-badge is-neutral">{policySourceLabel(p.source_type || "NONE")}</span>
        {(p.source_type || "NONE") === "FORMULA" && (
          <span className="policy-badge is-neutral">{policyFormulaLabel(p.formula_key || "NONE")}</span>
        )}
      </div>
    );
  }

  function renderPolicyCard(p) {
    const expanded = expandedPolicyKey === p.field_key;
    const formulaActive = (p.source_type || "NONE") === "FORMULA";
    const fieldId = `leave-policy-${p.field_key}`;

    return (
      <article key={p.field_key} className={`policy-card ${expanded ? "is-expanded" : ""}`}>
        <button
          type="button"
          className="policy-card-header"
          onClick={() => setExpandedPolicyKey(expanded ? "" : p.field_key)}
          aria-expanded={expanded}
          aria-controls={`${fieldId}-body`}
        >
          <div className="policy-card-title-block">
            <div className="policy-card-title-row">
              <strong>{policyFieldLabel(p.field_key)}</strong>
              <span className="policy-card-code">{p.field_key}</span>
            </div>
            <div className="policy-card-help">{policyFieldHelp(p.field_key)}</div>
          </div>
          <div className="policy-card-summary">
            {policyBadges(p)}
            <span className="policy-card-toggle">{expanded ? policyLabel("closeDetails") : policyLabel("openDetails")}</span>
          </div>
        </button>

        {expanded && (
          <div id={`${fieldId}-body`} className="policy-card-body">
            <section className="policy-card-section">
              <h4>{policyLabel("trackingAndAdjustment")}</h4>
              <div className="policy-toggle-grid">
                <label className="check-row">
                  <input
                    type="checkbox"
                    checked={asBool(p.tracks_value)}
                    onChange={(e) => patchPolicy(p.field_key, { tracks_value: e.target.checked })}
                  />
                  {policyLabel("track")}
                </label>
                <label className="check-row">
                  <input
                    type="checkbox"
                    checked={asBool(p.can_adjust_value)}
                    disabled={!asBool(p.tracks_value)}
                    onChange={(e) => patchPolicy(p.field_key, { can_adjust_value: e.target.checked })}
                  />
                  {policyLabel("canAdjust")}
                </label>
              </div>
            </section>

            <section className="policy-card-section">
              <h4>{policyLabel("calculationSource")}</h4>
              <div className="policy-card-grid two-col">
                <label className="vac-field">
                  <span>{policyLabel("source")}</span>
                  <select
                    value={p.source_type || "NONE"}
                    onChange={(e) => {
                      const nextSource = e.target.value;
                      patchPolicy(p.field_key, {
                        source_type: nextSource,
                        formula_key: nextSource === "FORMULA" ? (p.formula_key || "LEAVE_BALANCE_AVAILABLE") : "NONE",
                      });
                    }}
                    title={policySourceHelp(p.source_type)}
                  >
                    {SOURCE_TYPES.map((x) => <option key={x} value={x}>{policySourceLabel(x)}</option>)}
                  </select>
                  <div className="policy-cell-help">{policySourceHelp(p.source_type)}</div>
                </label>

                <label className="vac-field">
                  <span>{policyLabel("formula")}</span>
                  <select
                    value={p.formula_key || "NONE"}
                    disabled={!formulaActive}
                    onChange={(e) => patchPolicy(p.field_key, { formula_key: e.target.value })}
                    title={policyFormulaHelp(p.formula_key)}
                  >
                    {FORMULA_KEYS.map((x) => <option key={x} value={x}>{policyFormulaLabel(x)}</option>)}
                  </select>
                  <div className="policy-cell-help">
                    {formulaActive ? policyFormulaHelp(p.formula_key || "NONE") : policyLabel("noFormula")}
                  </div>
                </label>
              </div>

              {formulaActive && (
                <div className="formula-preview-box">
                  <strong>{policyLabel("formulaPreview")}: {policyFormulaLabel(p.formula_key || "NONE")}</strong>
                  <div>{policyFormulaHelp(p.formula_key || "NONE")}</div>
                </div>
              )}
            </section>

            <section className="policy-card-section">
              <h4>{policyLabel("defaultAndValidation")}</h4>
              <div className="policy-card-grid three-col">
                <label className="vac-field">
                  <span>{policyLabel("yearTransition")}</span>
                  <select
                    value={p.year_transition_mode || "NONE"}
                    disabled={p.field_key !== "OPENING_BALANCE"}
                    onChange={(e) => patchPolicy(p.field_key, { year_transition_mode: e.target.value })}
                  >
                    {YEAR_TRANSITION_MODES.map((x) => <option key={x} value={x}>{policyYearTransitionLabel(x)}</option>)}
                  </select>
                  <div className="policy-cell-help">
                    {p.field_key === "OPENING_BALANCE"
                      ? policyLabel("policyContractNote")
                      : ""}
                  </div>
                </label>
                <label className="vac-field"><span>{policyLabel("default")}</span><input type="number" step="0.01" value={p.default_value ?? ""} onChange={(e) => patchPolicy(p.field_key, { default_value: e.target.value })} /></label>
                <label className="vac-field"><span>{policyLabel("defaultDays")}</span><input type="number" step="0.01" value={p.default_value_days ?? ""} onChange={(e) => patchPolicy(p.field_key, { default_value_days: e.target.value })} /></label>
                <label className="vac-field"><span>{policyLabel("defaultHours")}</span><input type="number" step="0.01" value={p.default_value_hours ?? ""} onChange={(e) => patchPolicy(p.field_key, { default_value_hours: e.target.value })} /></label>
                <label className="vac-field"><span>{policyLabel("missing")}</span><select value={p.missing_value_policy || "USE_ZERO"} onChange={(e) => patchPolicy(p.field_key, { missing_value_policy: e.target.value })}>{MISSING_POLICIES.map((x) => <option key={x} value={x}>{policyMissingLabel(x)}</option>)}</select></label>
                <label className="vac-field"><span>{policyLabel("enforcement")}</span><select value={p.enforcement_mode || "NONE"} onChange={(e) => patchPolicy(p.field_key, { enforcement_mode: e.target.value })}>{ENFORCEMENT_MODES.map((x) => <option key={x} value={x}>{policyEnforcementLabel(x)}</option>)}</select></label>
              </div>
            </section>
          </div>
        )}
      </article>
    );
  }

  const editorModal = editorOpen ? createPortal(
    <div className="leave-type-modal-backdrop" role="presentation">
      <div
        className="leave-type-modal-card leave-type-editor"
        role="dialog"
        aria-modal="true"
        aria-labelledby="leave-type-editor-title"
      >
        <div className="modal-header">
          <h2 id="leave-type-editor-title">
            {editorMode === "CREATE"
              ? t("leaves:leaveTypes.createTitle", { defaultValue: "Create Leave Type" })
              : t("leaves:leaveTypes.editTitle", { defaultValue: "Edit Leave Type" })}
          </h2>
          <button type="button" className="icon-btn" onClick={() => setEditorOpen(false)}>×</button>
        </div>

        <div className="leave-type-modal-body">
          <div className="vac-grid">
            <label className="vac-field"><span>{policyLabel("code")}</span><input value={form.code} onChange={(e) => patch({ code: normalizeCode(e.target.value) })} /></label>
            <label className="vac-field"><span>{policyLabel("namePl")}</span><input value={form.name_pl} onChange={(e) => patch({ name_pl: e.target.value })} /></label>
            <label className="vac-field"><span>{policyLabel("nameEn")}</span><input value={form.name_en} onChange={(e) => patch({ name_en: e.target.value })} /></label>
            <label className="vac-field"><span>{policyLabel("unit")}</span><select value={form.unit} onChange={(e) => patch({ unit: e.target.value })}><option value="DAYS">{t("leaves:units.DAYS", { defaultValue: "Days" })}</option><option value="HOURS">{t("leaves:units.HOURS", { defaultValue: "Hours" })}</option></select></label>
            <label className="vac-field"><span>{policyLabel("accounting")}</span><select value={form.balance_accounting_type} onChange={(e) => patch({ balance_accounting_type: e.target.value })}>{ACCOUNTING_TYPES.map((x) => <option key={x} value={x}>{policyAccountingLabel(x)}</option>)}</select><div className="policy-cell-help">{policyAccountingHelp(form.balance_accounting_type)}</div></label>
            <label className="vac-field">
              <span>{policyLabel("balanceConsumption")}</span>
              <select
                value={form.balance_consumption_strategy || "CARRYOVER_FIRST"}
                onChange={(e) => patch({ balance_consumption_strategy: e.target.value })}
              >
                {BALANCE_CONSUMPTION_STRATEGIES.map((x) => (
                  <option key={x} value={x}>{policyConsumptionLabel(x)}</option>
                ))}
              </select>
              <div className="policy-cell-help">{policyConsumptionHelp(form.balance_consumption_strategy || "CARRYOVER_FIRST")}</div>
            </label>
            {form.balance_accounting_type === "DEDUCT_FROM_LEAVE_BALANCE" && (
              <label className="vac-field">
                <span>{policyLabel("balanceSource")}</span>
                <select
                  value={form.balance_source_leave_type_id || ""}
                  onChange={(e) => patch({ balance_source_leave_type_id: e.target.value })}
                >
                  <option value="">{policyLabel("balanceSourceOwn")}</option>
                  {balanceSourceOptions.map((x) => (
                    <option key={x.id} value={x.id}>{x.label}</option>
                  ))}
                </select>
                <div className="policy-cell-help">{policyLabel("balanceSourceHelp")}</div>
              </label>
            )}
            <label className="vac-field">
              <span>{policyLabel("usageModePolicy")}</span>
              <select
                value={form.usage_mode_policy || "NONE"}
                onChange={(e) => patch({ usage_mode_policy: e.target.value })}
              >
                {USAGE_MODE_POLICIES.map((x) => (
                  <option key={x} value={x}>{policyUsageModeLabel(x)}</option>
                ))}
              </select>
              <div className="policy-cell-help">{policyLabel("usageModePolicyHelp")}</div>
            </label>
          </div>

          <div className="vac-inline-checks" style={{ marginTop: 14 }}>
            {[
              ["active", policyLabel("active")],
              ["is_paid", policyLabel("paid")],
              ["requires_approval", policyLabel("requiresApproval")],
              ["supports_partial_day", policyLabel("partialDay")],
              ["show_in_calendar", policyLabel("showInCalendar")],
            ].map(([key, label]) => (
              <label key={key} className="check-row">
                <input
                  type="checkbox"
                  checked={key === "is_paid" ? Number(form.payroll_payment_rate_percent || 0) > 0 : asBool(form[key])}
                  onChange={(e) => {
                    if (key === "is_paid") {
                      setPayrollRate(e.target.checked ? (Number(form.payroll_payment_rate_percent || 0) > 0 ? form.payroll_payment_rate_percent : "100") : "0");
                      return;
                    }
                    patch({ [key]: e.target.checked });
                  }}
                />
                {" "}{label}
              </label>
            ))}
          </div>

          <div className="policy-editor-block">
            <div className="vac-card-title">{policyLabel("payrollSection")}</div>
            <p className="section-subtitle">{policyLabel("payrollHelp")}</p>
            <div className="policy-card-grid three-col">
              <label className="check-row">
                <input
                  type="checkbox"
                  checked={asBool(form.payroll_export_enabled)}
                  onChange={(e) => patch({ payroll_export_enabled: e.target.checked })}
                />
                {policyLabel("payrollExport")}
              </label>
              <label className="vac-field">
                <span>{policyLabel("payrollAbsenceCode")}</span>
                <input
                  value={form.payroll_absence_code || ""}
                  onChange={(e) => patch({ payroll_absence_code: normalizeCode(e.target.value).slice(0, 64) })}
                  placeholder={form.code || "FORCE_MAJEURE"}
                />
              </label>
              <label className="vac-field">
                <span>{policyLabel("payrollPaymentRatePreset")}</span>
                <select
                  value={inferPayrollRatePreset(form.payroll_payment_rate_percent)}
                  onChange={(e) => applyPayrollPreset(e.target.value)}
                >
                  {PAYROLL_RATE_PRESETS.map((p) => (
                    <option key={p.value} value={p.value}>{policyLabel(p.labelKey)}</option>
                  ))}
                </select>
              </label>
              <label className="vac-field">
                <span>{policyLabel("payrollPaymentRate")}</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={form.payroll_payment_rate_percent ?? ""}
                  onChange={(e) => setPayrollRate(e.target.value)}
                />
              </label>
            </div>
          </div>

          <div className="policy-editor-block">
            <div className="vac-card-title">{policyLabel("annualSummaryFieldPolicies")}</div>
            <p className="section-subtitle">
              {policyLabel("methodologyNote")}<br />
              {policyLabel("policyContractNote")}
            </p>
            <div className="policy-help-box">
              <div><strong>{policyLabel("currentRecommendation")}:</strong> {policyRecommendation(form.balance_accounting_type)}</div>
              <div className="policy-help-grid">
                <div><strong>{policySourceLabel("REQUESTS_EFFECTIVE")}</strong><br />{policySourceHelp("REQUESTS_EFFECTIVE")}</div>
                <div><strong>{policyLabel("formula")}: {policyFormulaLabel("LEAVE_BALANCE_AVAILABLE")}</strong><br />{policyFormulaHelp("LEAVE_BALANCE_AVAILABLE")}</div>
                <div><strong>{policyLabel("formula")}: {policyFormulaLabel("ANNUAL_LIMIT_AVAILABLE")}</strong><br />{policyFormulaHelp("ANNUAL_LIMIT_AVAILABLE")}</div>
              </div>
              <div className="vac-small">
                {policyLabel("legalHrNote")}
              </div>
            </div>
            <button type="button" className="mode-btn" onClick={applyRecommendedPolicies}>{policyLabel("applyRecommended")}</button>
            <div className="policy-cards-intro">{policyLabel("policyCardsIntro")}</div>
            <div className="policy-cards-wrap">
              {normalizedPolicies.map((p) => renderPolicyCard(p))}
            </div>          </div>
        </div>

        <div className="modal-actions">
          <button type="button" className="mode-btn" onClick={() => setEditorOpen(false)}>{t("common:actions.cancel", { defaultValue: "Cancel" })}</button>
          <button type="button" className="primary-btn" onClick={submit}>{t("common:actions.save", { defaultValue: "Save" })}</button>
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <div className="leave-types-page">
      <div className="page-header-row">
        <div>
          <h1>{t("leaves:leaveTypes.title", { defaultValue: "Leave Types" })}</h1>
          <p className="section-subtitle">
            {t("leaves:leaveTypes.subtitle", {
              defaultValue: "Master data for HR/Leave request types and their annual summary field policies.",
            })}
          </p>
        </div>
        <div className="header-actions">
          <button type="button" className="mode-btn" onClick={() => navigate("/dashboard")}>{t("common:actions.back", { defaultValue: "Back" })}</button>
          <button type="button" className="primary-btn" onClick={openCreate}>{t("common:actions.add", { defaultValue: "Add" })}</button>
        </div>
      </div>

      {error && <div className="form-error">{error}</div>}
      {success && <div className="form-success">{success}</div>}

      <div className="vac-card">
        <div className="vac-card-title">
          {t("leaves:leaveTypes.activeCount", { count: activeCount, defaultValue: `Active leave types: ${activeCount}` })}
        </div>
        <div className="table-scroll-wrap">
          <table className="leave-types-table">
            <thead>
              <tr>
                <th>{t("leaves:leaveTypes.columns.code", { defaultValue: "Code" })}</th>
                <th>{t("leaves:leaveTypes.columns.name", { defaultValue: "Name" })}</th>
                <th>{t("leaves:leaveTypes.columns.accounting", { defaultValue: "Accounting" })}</th>
                <th>{t("leaves:leaveTypes.columns.balanceConsumption", { defaultValue: "Balance consumption" })}</th>
                <th>{t("leaves:leaveTypes.columns.unit", { defaultValue: "Unit" })}</th>
                <th>{policyLabel("payrollSection")}</th>
                <th>{t("leaves:leaveTypes.columns.policy", { defaultValue: "Tracked / adjustable" })}</th>
                <th>{t("leaves:leaveTypes.columns.active", { defaultValue: "Active" })}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9}>{t("common:messages.loading", { defaultValue: "Loading..." })}</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={9}>{t("common:messages.noData", { defaultValue: "No data." })}</td></tr>
              ) : rows.map((r) => (
                <tr key={r.id}>
                  <td><b>{r.code}</b></td>
                  <td>{r.name_pl}<div className="vac-small">{r.name_en}</div></td>
                  <td>{policyAccountingLabel(r.balance_accounting_type)}<div className="vac-small">{r.balance_accounting_type}</div></td>
                  <td>
                    {policyConsumptionLabel(r.balance_consumption_strategy || "NONE")}<div className="vac-small">{r.balance_consumption_strategy || "NONE"}</div>
                    <div className="vac-small">{policyLabel("balanceSource")}: {formatBalanceSource(r)}</div>
                  </td>
                  <td>{t(`leaves:units.${r.unit}`, { defaultValue: r.unit })}</td>
                  <td>
                    {Number(r.payroll_payment_rate_percent ?? (asBool(r.is_paid) ? 100 : 0)).toFixed(2)}%
                    <div className="vac-small">{r.payroll_absence_code || r.code}</div>
                  </td>
                  <td>{policySummary(r)}</td>
                  <td>{asBool(r.active) ? "✓" : "—"}</td>
                  <td className="actions-cell">
                    <button type="button" className="mode-btn" onClick={() => openEdit(r)}>{t("common:actions.edit", { defaultValue: "Edit" })}</button>
                    <button type="button" className="mode-btn" onClick={() => toggle(r)}>{asBool(r.active) ? t("common:actions.deactivate", { defaultValue: "Deactivate" }) : t("common:actions.activate", { defaultValue: "Activate" })}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editorModal}
    </div>
  );
}


